import React from "react"
import { formatMoney, formatDateTime, formatDiscount } from "../transactions/format"
import { MerchantCell } from "../shared/MerchantLabel"

const STATUS_STYLES = {
  active: "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 ring-green-600/20",
  frozen: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 ring-amber-600/20",
  cancelled: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-500/20",
  declined: "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 ring-red-600/20",
  expired: "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 ring-gray-400/20",
}

function StatusPill({ status, copy }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.cancelled
  const label = copy.statuses?.[status] || status
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {label}
    </span>
  )
}

function intervalLabel(interval, copy) {
  return copy.intervals?.[interval] || interval || "—"
}

function DiscountBadge({ row, copy }) {
  const discount = formatDiscount(row)
  if (!discount) return null
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">
      {copy.discountBadge}: {discount}
    </span>
  )
}

// Responsive: real table on md+, stacked cards on mobile.
export function SubscriptionsTable({ rows, copy, detailBasePath }) {
  return (
    <>
      {/* Desktop / tablet table */}
      <div className="hidden overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800 md:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="px-4 py-3">{copy.columns.shop}</th>
              <th className="px-4 py-3">{copy.columns.app}</th>
              <th className="px-4 py-3">{copy.columns.plan}</th>
              <th className="px-4 py-3 text-right">{copy.columns.price}</th>
              <th className="px-4 py-3">{copy.columns.interval}</th>
              <th className="px-4 py-3">{copy.columns.status}</th>
              <th className="px-4 py-3">{copy.columns.activated_at}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                  <MerchantCell name={r.name} shop={r.shop} merchantId={r.merchant_id} detailBasePath={detailBasePath} />
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.app || "—"}</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  {r.plan || "—"}
                  <DiscountBadge row={r} copy={copy} />
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-gray-100">
                  {formatMoney(r.price_cents, r.currency)}
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{intervalLabel(r.interval, copy)}</td>
                <td className="px-4 py-3">
                  <StatusPill status={r.status} copy={copy} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                  {formatDateTime(r.activated_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <MerchantCell name={r.name} shop={r.shop} merchantId={r.merchant_id} detailBasePath={detailBasePath} />
                <div className="text-xs text-gray-500 dark:text-gray-400">{r.app || "—"}</div>
              </div>
              <StatusPill status={r.status} copy={copy} />
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
              <dt className="text-gray-400 dark:text-gray-500">{copy.columns.plan}</dt>
              <dd className="text-right">
                {r.plan || "—"}
                <DiscountBadge row={r} copy={copy} />
              </dd>
              <dt className="text-gray-400 dark:text-gray-500">{copy.columns.price}</dt>
              <dd className="text-right tabular-nums">{formatMoney(r.price_cents, r.currency)}</dd>
              <dt className="text-gray-400 dark:text-gray-500">{copy.columns.interval}</dt>
              <dd className="text-right">{intervalLabel(r.interval, copy)}</dd>
              <dt className="text-gray-400 dark:text-gray-500">{copy.columns.activated_at}</dt>
              <dd className="text-right">{formatDateTime(r.activated_at)}</dd>
            </dl>
          </div>
        ))}
      </div>
    </>
  )
}
